import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { AIThinkingState } from "@/components/AltioraComponents";
import { toast } from "sonner";
import {
  Sparkles, Compass, MapPin, Clock, Coins, TrendingUp,
  Users2, ExternalLink, Plus, Filter as FilterIcon, CheckCircle2, ChevronRight,
  Zap, Gauge, X, Lightbulb,
} from "lucide-react";
import { useUserLocation } from "@/hooks/useUserLocation";
import DossierTimeline from "@/components/altiora/DossierTimeline";
import RelationshipTimeline from "@/components/altiora/RelationshipTimeline";

// ---------------------------------------------------------------------------
// Constants & visual metadata
// ---------------------------------------------------------------------------
const BADGE_META = {
  verified:            { dot: "🟢", label: "Vérifié",                   ring: "bg-[#059669]",  chip: "text-[#065F46] bg-[#D1FAE5]" },
  cross_checked:       { dot: "🟡", label: "Croisé (plusieurs sources)", ring: "bg-[#F59E0B]",  chip: "text-[#B45309] bg-[#FEF3C7]" },
  community:           { dot: "🔵", label: "Contribution communautaire", ring: "bg-[#0B2545]",  chip: "text-[#0B2545] bg-[#E0E7FF]" },
  weak_but_promising:  { dot: "🟣", label: "Signal faible mais prometteur", ring: "bg-[#7C3AED]", chip: "text-[#5B21B6] bg-[#EDE9FE]" },
};

// HOE™ Hidden Opportunity Engine — 5-tier visibility taxonomy
const VISIBILITY_META = {
  1: { emoji: "🌐", label: "Visible par tous",                     chip: "text-[#5B5B62] bg-[#F1EFE9]" },
  2: { emoji: "🌤️", label: "Visible mais rarement cherchée",       chip: "text-[#0B2545] bg-[#E0E7FF]" },
  3: { emoji: "🔒", label: "Accès conditionné",                    chip: "text-[#7A5B14] bg-[#FEF3C7]" },
  4: { emoji: "🗝️", label: "Cachée — intermédiaire requis",        chip: "text-[#5B21B6] bg-[#EDE9FE]" },
  5: { emoji: "🔮", label: "Prédite par ALTIORA",                  chip: "text-white bg-gradient-to-r from-[#C5A880] to-[#8B7355]" },
};

const URGENCY_LABEL = {
  immediate: "Immédiat", this_week: "Cette semaine", this_month: "Ce mois",
  this_quarter: "Ce trimestre", flexible: "Flexible",
};
const COST_LABEL = { free: "Gratuit", low: "Faible", medium: "Moyen", high: "Élevé" };
const DIFFICULTY_LABEL = { easy: "Facile", moderate: "Modéré", challenging: "Difficile", expert: "Expert" };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function Discovery() {
  useI18n();
  const [feed, setFeed] = useState(null);
  const [surprise, setSurprise] = useState(null);
  const [trust, setTrust] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [selected, setSelected] = useState(null); // detail drawer
  const [predicting, setPredicting] = useState(false);
  const [f, setF] = useState({
    category: "", city: "", radius_km: "", confidence_badge: "", visibility_level: "",
    urgency: "", cost_level: "", difficulty_level: "",
    opportunity_type: "", partner_name: "", org_name: "",
    language: "", target_public: "", economic_impact_min: "",
  });

  // ---- Load ----
  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v) params.set(k, v); });
      const [feedR, surpR, trustR] = await Promise.all([
        api.get(`/discovery/feed?${params.toString()}`),
        api.get("/discovery/surprise"),
        api.get("/discovery/me/trust"),
      ]);
      setFeed(feedR.data);
      setSurprise(surpR.data);
      setTrust(trustR.data);
    } catch (e) {
      toast.error("Impossible de charger le Discovery Engine");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const categoriesMeta = feed?.categories || [];

  // ---- Confirm / outcome actions ----
  const confirmSignal = async (id) => {
    try {
      const r = await api.post(`/discovery/${id}/confirm`);
      toast.success(`Confirmé (${r.data.confirmations} confirmations)`);
      refresh();
    } catch { toast.error("Échec"); }
  };
  const submitOutcome = async (id, outcome) => {
    try {
      await api.post(`/discovery/${id}/outcome`, { outcome });
      toast.success("Résultat enregistré — merci !");
      refresh();
    } catch { toast.error("Échec"); }
  };

  const runPredict = async () => {
    setPredicting(true);
    try {
      const r = await api.post("/discovery/predict");
      toast.success(`${r.data.generated} opportunité(s) prédite(s) par ${r.data.llm_used}`);
      refresh();
    } catch { toast.error("Prédiction indisponible pour le moment"); }
    finally { setPredicting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="discovery-root">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <Compass className="h-5 w-5 text-navy" strokeWidth={2} />
              <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy">Discovery Engine · ODS™</div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">
              Découvertes cachées
            </h1>
            <p className="text-base md:text-lg text-[#374151] mt-5 max-w-2xl leading-relaxed">
              Le moteur décide de l&apos;ordre. Vous découvrez ce qui peut réellement changer votre trajectoire.
              La première carte est celle au plus fort potentiel de transformation pour vous.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runPredict}
              disabled={predicting}
              data-testid="discovery-predict-btn"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm bg-gradient-to-r from-[#C5A880] to-[#8B7355] text-white hover:from-[#B69670] hover:to-[#7A6449] disabled:opacity-50 transition"
              title="HOE™ — Prédit des opportunités cachées par croisement de vos données ALTIORA"
            >
              <Sparkles className={`h-4 w-4 ${predicting ? "animate-pulse" : ""}`} strokeWidth={1.5} />
              {predicting ? "Prédiction…" : "🔮 Prédire (HOE™)"}
            </button>
            <button
              onClick={() => setShowFilters((v) => !v)}
              data-testid="discovery-filters-toggle"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm border border-[#E4E1D8] bg-white text-[#1F2937] hover:bg-[#F1EFE9] transition"
            >
              <FilterIcon className="h-4 w-4" strokeWidth={1.5} /> Filtres
            </button>
            <button
              onClick={() => setShowSubmit(true)}
              data-testid="discovery-submit-open"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm bg-[#0B2545] text-white hover:bg-[#123163] transition"
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} /> Soumettre un signal
            </button>
          </div>
        </div>

        {/* Trust widget */}
        {trust && (
          <div className="mb-6 bg-white border border-[#E4E1D8] rounded-lg p-4 flex items-center justify-between flex-wrap gap-4" data-testid="discovery-trust-widget">
            <div className="flex items-center gap-4">
              <div className="relative w-14 h-14 rounded-full bg-[#F1EFE9] flex items-center justify-center border border-[#E4E1D8]">
                <div className="text-lg font-display font-medium text-[#0B2545]">{trust.trust_score}</div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Mon Trust Score</div>
                <div className="text-sm text-[#1F2937]">
                  {trust.total_signals} signaux · {trust.verified_count} vérifiés · {trust.positive_outcomes} outcomes positifs
                </div>
              </div>
            </div>
            <div className="text-[11px] font-mono text-[#8B8B8F] flex items-center gap-3">
              <span>vér. {Math.round(trust.breakdown.verified_ratio*100)}%</span>
              <span>·</span>
              <span>précision {Math.round(trust.breakdown.accuracy_ratio*100)}%</span>
              <span>·</span>
              <span>ancienneté {trust.breakdown.tenure_months}m</span>
            </div>
          </div>
        )}

        {/* Filters panel */}
        {showFilters && (
          <FiltersPanel
            f={f} setF={setF}
            categories={categoriesMeta}
            onApply={() => { setShowFilters(false); refresh(); }}
            onReset={() => {
              setF({ category:"",city:"",radius_km:"",confidence_badge:"",visibility_level:"",urgency:"",cost_level:"",difficulty_level:"",opportunity_type:"",partner_name:"",org_name:"",language:"",target_public:"",economic_impact_min:"" });
              setTimeout(refresh, 0);
            }}
          />
        )}

        {/* Surprise-Me card */}
        {surprise?.available && surprise?.item && (
          <SurpriseCard item={surprise.item} onOpen={() => setSelected(surprise.item)} />
        )}

        {/* Feed */}
        {loading ? (
          <AIThinkingState label="Analyse des opportunités cachées…" />
        ) : (feed?.items?.length ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="discovery-feed">
            {feed.items.map((s, idx) => (
              <DiscoveryCard
                key={s.id}
                signal={s}
                highlight={idx === 0}
                onOpen={() => setSelected(s)}
                onConfirm={() => confirmSignal(s.id)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-[#5B5B62] bg-white border border-[#E4E1D8] rounded-lg">
            Aucun signal pour ces filtres — élargissez la recherche.
          </div>
        ))}

        {/* Detail drawer */}
        {selected && (
          <DetailDrawer
            signal={selected}
            onClose={() => setSelected(null)}
            onConfirm={confirmSignal}
            onOutcome={submitOutcome}
          />
        )}

        {/* Submit modal */}
        {showSubmit && (
          <SubmitModal
            categories={categoriesMeta}
            onClose={() => setShowSubmit(false)}
            onSubmitted={() => { setShowSubmit(false); refresh(); }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Surprise card (weekly)
// ---------------------------------------------------------------------------
function SurpriseCard({ item, onOpen }) {
  return (
    <div
      className="mb-6 relative overflow-hidden rounded-xl border-2 border-[#C5A880]/60 bg-gradient-to-br from-[#FBF6EA] to-white p-5 cursor-pointer group"
      onClick={onOpen}
      data-testid="discovery-surprise-card"
    >
      <div className="absolute -right-8 -top-8 w-32 h-32 bg-[#C5A880]/10 rounded-full" />
      <div className="relative flex items-start gap-4">
        <div className="h-11 w-11 rounded-md bg-[#C5A880]/15 flex items-center justify-center flex-shrink-0">
          <Lightbulb className="h-5 w-5 text-[#7A5B14]" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#7A5B14] mb-1">
            💡 Opportunité inattendue détectée
          </div>
          <h3 className="text-lg font-display font-medium text-[#1F2937] tracking-tight mb-1">
            {item.title_fr}
          </h3>
          <p className="text-sm text-[#5B5B62] italic mb-2">« {item.surprise_headline_fr} »</p>
          <div className="flex items-center gap-3 text-xs text-[#5B5B62]">
            <span className="inline-flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> ODS™ {item.ods_score}</span>
            {item.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {item.city}</span>}
            {item.economic_impact_estimate && <span className="inline-flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> {item.economic_impact_estimate}</span>}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-[#C5A880] mt-2 group-hover:translate-x-1 transition" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Discovery card
// ---------------------------------------------------------------------------
function DiscoveryCard({ signal, highlight, onOpen, onConfirm }) {
  const badge = BADGE_META[signal.confidence_badge] || BADGE_META.community;
  const vmeta = VISIBILITY_META[signal.visibility_level] || VISIBILITY_META[1];
  const isPredicted = signal.visibility_level === 5;
  return (
    <div
      className={`bg-white border rounded-xl p-5 hover:shadow-md transition cursor-pointer relative ${
        isPredicted ? "border-[#C5A880] ring-2 ring-[#C5A880]/25" : highlight ? "border-[#0B2545] ring-1 ring-[#0B2545]/15" : "border-[#E4E1D8]"
      }`}
      data-testid="discovery-card"
      onClick={onOpen}
    >
      {isPredicted && (
        <div className="absolute -top-2 left-4 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.22em] bg-gradient-to-r from-[#C5A880] to-[#8B7355] text-white rounded">
          🔮 Prédite par ALTIORA · Niv. 5
        </div>
      )}
      {!isPredicted && highlight && (
        <div className="absolute -top-2 left-4 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.22em] bg-[#0B2545] text-white rounded">
          Top match pour vous
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-display font-medium text-[#1F2937] tracking-tight leading-snug">
            {signal.title_fr}
          </h3>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ${badge.chip}`}>
              <span>{badge.dot}</span> {badge.label}
            </span>
            <span
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ${vmeta.chip}`}
              title={`HOE™ visibilité niveau ${signal.visibility_level}`}
              data-testid="visibility-badge"
            >
              <span>{vmeta.emoji}</span> Niv. {signal.visibility_level}
            </span>
            {signal.city && (
              <span className="text-xs text-[#5B5B62] inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {signal.city}
                {signal.distance_km !== undefined && ` · ${signal.distance_km} km`}
              </span>
            )}
          </div>
        </div>
        <ScoreBadge value={signal.ods_score} />
      </div>

      <p className="text-sm text-[#5B5B62] line-clamp-2 mb-3">{signal.description_fr}</p>

      {/* Why predicted (Level 5 only) */}
      {isPredicted && (signal.why_predicted_fr || signal.cross_references_fr?.length) && (
        <div className="bg-gradient-to-r from-[#FBF6EA] to-[#F8F7F3] rounded-md px-3 py-2 mb-3 border-l-2 border-[#C5A880]">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7A5B14] mb-1">Pourquoi ALTIORA l&apos;a prédite</div>
          {signal.why_predicted_fr && (
            <p className="text-xs text-[#1F2937] italic mb-1">« {signal.why_predicted_fr} »</p>
          )}
          {signal.cross_references_fr?.length > 0 && (
            <div className="text-[10px] text-[#5B5B62]">
              Croisement : {signal.cross_references_fr.slice(0, 2).join(" · ")}
            </div>
          )}
        </div>
      )}

      {/* Why for you */}
      {signal.why_for_you?.length > 0 && (
        <div className="bg-[#F1EFE9] rounded-md px-3 py-2 mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-1">Pourquoi pour vous</div>
          <ul className="text-xs text-[#1F2937] space-y-0.5">
            {signal.why_for_you.map((r, i) => (
              <li key={i} className="flex items-start gap-1"><span className="text-[#C5A880]">·</span> {r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Meta strip */}
      <div className="flex items-center gap-3 flex-wrap text-[11px] text-[#5B5B62]">
        {signal.economic_impact_estimate && (
          <span className="inline-flex items-center gap-1"><TrendingUp className="h-3 w-3 text-[#059669]" /> {signal.economic_impact_estimate}</span>
        )}
        <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3 text-[#F59E0B]" /> {URGENCY_LABEL[signal.urgency] || signal.urgency}</span>
        <span className="inline-flex items-center gap-1"><Gauge className="h-3 w-3" /> {DIFFICULTY_LABEL[signal.difficulty_level] || signal.difficulty_level}</span>
        {signal.estimated_time_hours != null && (
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> ~{signal.estimated_time_hours}h</span>
        )}
      </div>

      {/* Community proof */}
      <div className="mt-3 pt-3 border-t border-[#E4E1D8] flex items-center justify-between text-[11px] text-[#5B5B62]">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><Users2 className="h-3 w-3" /> {signal.community_proof.partners_confirmed} confirmations</span>
          {signal.community_proof.interviews_obtained > 0 && <span>· {signal.community_proof.interviews_obtained} entrevues</span>}
          {signal.community_proof.hires > 0 && <span>· {signal.community_proof.hires} embauches</span>}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onConfirm(); }}
          className="text-[#0B2545] hover:text-[#123163] inline-flex items-center gap-1 font-medium"
          data-testid="discovery-confirm-btn"
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Confirmer
        </button>
      </div>
    </div>
  );
}

function ScoreBadge({ value }) {
  const color = value >= 75 ? "#0B2545" : value >= 55 ? "#7A5B14" : "#5B5B62";
  return (
    <div className="flex flex-col items-center justify-center min-w-[54px] h-14 rounded-md border border-[#E4E1D8] bg-[#FBF6EA]">
      <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#5B5B62]">ODS</div>
      <div className="text-xl font-display font-medium leading-none" style={{ color }}>{value}</div>
    </div>
  );
}

// Small label helper used across filter and submit forms
const FormLabel = ({ children }) => (
  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-1">{children}</div>
);

// ---------------------------------------------------------------------------
// Filters panel
// ---------------------------------------------------------------------------
function FiltersPanel({ f, setF, categories, onApply, onReset }) {
  const { city: userCity } = useUserLocation();
  const cityHint = userCity ? `${userCity}, autres villes…` : "Votre ville, autres…";
  const set = (k, v) => setF({ ...f, [k]: v });
  const S = FormLabel;
  const inputCls = "w-full px-2.5 py-1.5 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]";
  return (
    <div className="mb-6 bg-white border border-[#E4E1D8] rounded-lg p-5" data-testid="discovery-filters-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><S>Catégorie</S>
          <select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="filter-category">
            <option value="">Toutes</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label_fr}</option>)}
          </select>
        </div>
        <div><S>Ville</S><input className={inputCls} placeholder={cityHint} value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="filter-city" /></div>
        <div><S>Rayon (km)</S><input type="number" className={inputCls} value={f.radius_km} onChange={(e) => set("radius_km", e.target.value)} data-testid="filter-radius" /></div>
        <div><S>Niveau de confiance</S>
          <select className={inputCls} value={f.confidence_badge} onChange={(e) => set("confidence_badge", e.target.value)} data-testid="filter-badge">
            <option value="">Tous</option>
            <option value="verified">🟢 Vérifié</option>
            <option value="cross_checked">🟡 Croisé</option>
            <option value="community">🔵 Communautaire</option>
            <option value="weak_but_promising">🟣 Signal faible</option>
          </select>
        </div>
        <div><S>Visibilité (HOE™)</S>
          <select className={inputCls} value={f.visibility_level} onChange={(e) => set("visibility_level", e.target.value)} data-testid="filter-visibility">
            <option value="">Tous niveaux</option>
            <option value="1">🌐 Niv. 1 · Visible par tous</option>
            <option value="2">🌤️ Niv. 2 · Rarement cherchée</option>
            <option value="3">🔒 Niv. 3 · Accès conditionné</option>
            <option value="4">🗝️ Niv. 4 · Intermédiaire requis</option>
            <option value="5">🔮 Niv. 5 · Prédite par ALTIORA</option>
          </select>
        </div>
        <div><S>Urgence</S>
          <select className={inputCls} value={f.urgency} onChange={(e) => set("urgency", e.target.value)}>
            <option value="">Toutes</option>
            {Object.entries(URGENCY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div><S>Coût</S>
          <select className={inputCls} value={f.cost_level} onChange={(e) => set("cost_level", e.target.value)}>
            <option value="">Tous</option>
            {Object.entries(COST_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div><S>Difficulté</S>
          <select className={inputCls} value={f.difficulty_level} onChange={(e) => set("difficulty_level", e.target.value)}>
            <option value="">Toutes</option>
            {Object.entries(DIFFICULTY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div><S>Impact éco. min (0-100)</S><input type="number" min="0" max="100" className={inputCls} value={f.economic_impact_min} onChange={(e) => set("economic_impact_min", e.target.value)} /></div>
        <div><S>Type opportunité</S><input className={inputCls} placeholder="job, grant, event…" value={f.opportunity_type} onChange={(e) => set("opportunity_type", e.target.value)} /></div>
        <div><S>Partenaire</S><input className={inputCls} value={f.partner_name} onChange={(e) => set("partner_name", e.target.value)} /></div>
        <div><S>Organisme</S><input className={inputCls} value={f.org_name} onChange={(e) => set("org_name", e.target.value)} /></div>
        <div><S>Public cible</S>
          <select className={inputCls} value={f.target_public} onChange={(e) => set("target_public", e.target.value)}>
            <option value="">Tous</option>
            <option value="newcomer">Nouveaux arrivants</option>
            <option value="student">Étudiants</option>
            <option value="professional">Professionnels</option>
            <option value="entrepreneur">Entrepreneurs</option>
            <option value="senior">Seniors</option>
            <option value="minority">Minorités</option>
          </select>
        </div>
        <div><S>Langue</S>
          <select className={inputCls} value={f.language} onChange={(e) => set("language", e.target.value)}>
            <option value="">Toutes</option>
            <option value="fr">Français</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-4">
        <button onClick={onReset} className="px-3 py-1.5 text-sm text-[#5B5B62] hover:text-[#1F2937]" data-testid="filters-reset">Réinitialiser</button>
        <button onClick={onApply} className="px-4 py-1.5 text-sm bg-[#0B2545] text-white rounded-md hover:bg-[#123163]" data-testid="filters-apply">Appliquer</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------
function DetailDrawer({ signal, onClose, onConfirm, onOutcome }) {
  const badge = BADGE_META[signal.confidence_badge] || BADGE_META.community;
  const vmeta = VISIBILITY_META[signal.visibility_level] || VISIBILITY_META[1];
  const [hoe, setHoe] = useState(null);
  const [loadingHoe, setLoadingHoe] = useState(false);
  const loadHoe = async () => {
    setLoadingHoe(true);
    try {
      const r = await api.get(`/discovery/${signal.id}/hoe-explain`);
      setHoe(r.data.explanation);
    } catch { toast.error("HOE indisponible pour le moment"); }
    finally { setLoadingHoe(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="discovery-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full md:w-[560px] bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#5B5B62]">Signal Discovery · HOE™</div>
          <button onClick={onClose} data-testid="drawer-close" className="p-1 text-[#5B5B62] hover:text-[#1F2937]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <div className="flex items-start gap-3">
            <ScoreBadge value={signal.ods_score} />
            <div className="flex-1">
              <h2 className="text-xl font-display font-medium text-[#1F2937] tracking-tight">{signal.title_fr}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ${badge.chip}`}>
                  <span>{badge.dot}</span> {badge.label}
                </span>
                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ${vmeta.chip}`}>
                  {vmeta.emoji} {vmeta.label}
                </span>
                {signal.city && <span className="text-xs text-[#5B5B62]"><MapPin className="inline h-3 w-3" /> {signal.city}{signal.distance_km !== undefined ? ` · ${signal.distance_km} km` : ""}</span>}
              </div>
            </div>
          </div>

          <p className="text-sm text-[#1F2937] leading-relaxed">{signal.description_fr}</p>

          {/* Sprint 10.N · Chronologie du dossier — intelligence temporelle */}
          {(signal.created_at || signal.event_at || signal.expires_at) && (
            <DossierTimeline
              testid="discovery-drawer-timeline"
              createdAt={signal.created_at}
              nextStep={signal.event_at ? {
                label: "Événement / moment-clé",
                hint: new Date(signal.event_at).toLocaleString("fr-CA", {
                  dateStyle: "medium", timeStyle: "short",
                }),
              } : null}
              nextDeadline={signal.expires_at ? {
                label: "Fenêtre optimale d'action",
                at: signal.expires_at,
                criticality: (() => {
                  const hoursLeft = (new Date(signal.expires_at) - new Date()) / 3600000;
                  if (hoursLeft <= 4) return "critical_now";
                  if (hoursLeft <= 24) return "important_today";
                  if (hoursLeft <= 24 * 7) return "to_plan";
                  return "can_wait";
                })(),
              } : null}
            />
          )}

          {/* CP-B · Universal Relationship Timeline (recommandations, décisions, contrats…) */}
          {signal.id && (
            <div className="border border-[#C5A880]/40 rounded-md p-3 bg-white mt-3">
              <RelationshipTimeline subjectType="deal" subjectId={signal.id} />
            </div>
          )}

          {/* Level 5 prediction context */}
          {signal.visibility_level === 5 && signal.why_predicted_fr && (
            <div className="bg-gradient-to-br from-[#FBF6EA] to-white border border-[#C5A880]/40 rounded-md p-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7A5B14] mb-1">🔮 Pourquoi ALTIORA l&apos;a prédite</div>
              <p className="text-sm text-[#1F2937] italic mb-2">« {signal.why_predicted_fr} »</p>
              {signal.cross_references_fr?.length > 0 && (
                <div className="text-[11px] text-[#5B5B62]">
                  <span className="font-medium">Sources croisées :</span>
                  <ul className="list-disc list-inside mt-0.5">
                    {signal.cross_references_fr.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* HOE™ 8-point explanation (on demand) */}
          <div className="border border-[#C5A880]/40 rounded-md p-3 bg-white" data-testid="hoe-explain-block">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7A5B14]">
                🕵️ Hidden Opportunity Engine — Analyse 8 points
              </div>
              {!hoe && (
                <button
                  onClick={loadHoe}
                  disabled={loadingHoe}
                  className="text-[11px] px-2.5 py-1 rounded bg-gradient-to-r from-[#C5A880] to-[#8B7355] text-white hover:opacity-90 disabled:opacity-50"
                  data-testid="hoe-explain-btn"
                >
                  {loadingHoe ? "Analyse IA…" : "Révéler l'analyse HOE™"}
                </button>
              )}
            </div>
            {hoe && (
              <div className="space-y-2 text-sm text-[#1F2937]">
                {[
                  ["Pourquoi cachée", hoe.why_hidden_fr],
                  ["Pourquoi pour VOUS", hoe.why_for_you_hoe_fr],
                  ["Pourquoi peu la voient", hoe.why_few_see_it_fr],
                  ["ROI potentiel", hoe.roi_estimate_fr],
                  ["Difficulté analysée", hoe.difficulty_analysis_fr],
                ].map(([label, val], i) => val ? (
                  <div key={i}>
                    <div className="text-[10px] font-mono uppercase tracking-wide text-[#5B5B62]">{label}</div>
                    <div className="text-[13px] leading-relaxed">{val}</div>
                  </div>
                ) : null)}
                {hoe.access_actions_fr?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wide text-[#5B5B62]">Actions pour y accéder</div>
                    <ol className="text-[13px] list-decimal list-inside space-y-0.5">
                      {hoe.access_actions_fr.map((a, i) => <li key={i}>{a}</li>)}
                    </ol>
                  </div>
                )}
                {hoe.helpful_partners_fr?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wide text-[#5B5B62]">Partenaires utiles</div>
                    <ul className="text-[13px] list-disc list-inside">
                      {hoe.helpful_partners_fr.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                )}
                {hoe.success_content_ideas_fr?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-wide text-[#5B5B62]">Contenus pour réussir</div>
                    <ul className="text-[13px] list-disc list-inside">
                      {hoe.success_content_ideas_fr.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
                <div className="text-[10px] text-[#8B8B8F] pt-1 border-t border-[#F1EFE9]">
                  Analyse générée par {hoe._llm || "IA ALTIORA"}
                </div>
              </div>
            )}
          </div>

          {/* Why for you */}
          {signal.why_for_you?.length > 0 && (
            <div className="bg-[#F1EFE9] rounded-md p-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-1">Pourquoi cette opportunité apparaît pour vous</div>
              <ul className="text-sm text-[#1F2937] space-y-1">
                {signal.why_for_you.map((r, i) => <li key={i} className="flex items-start gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#C5A880] mt-0.5" /> {r}</li>)}
              </ul>
            </div>
          )}

          {/* Actions to take */}
          {signal.actions_to_take?.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-2">Actions à réaliser</div>
              <ol className="text-sm text-[#1F2937] space-y-1.5 list-decimal list-inside">
                {signal.actions_to_take.map((a, i) => <li key={i}>{a}</li>)}
              </ol>
            </div>
          )}

          {/* Resources */}
          {signal.resources?.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-2">Ressources associées</div>
              <ul className="text-sm space-y-1">
                {signal.resources.map((r, i) => (
                  <li key={i}>
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[#0B2545] hover:text-[#123163] inline-flex items-center gap-1">
                      <ExternalLink className="h-3.5 w-3.5" /> {r.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <MetaRow icon={TrendingUp} label="Impact économique" value={signal.economic_impact_estimate || `${signal.economic_potential}/100`} />
            <MetaRow icon={Zap} label="Urgence" value={URGENCY_LABEL[signal.urgency] || signal.urgency} />
            <MetaRow icon={Coins} label="Coût" value={COST_LABEL[signal.cost_level] || signal.cost_level} />
            <MetaRow icon={Gauge} label="Difficulté d'accès" value={DIFFICULTY_LABEL[signal.difficulty_level] || signal.difficulty_level} />
            {signal.estimated_time_hours != null && <MetaRow icon={Clock} label="Temps estimé" value={`~${signal.estimated_time_hours}h`} />}
            {signal.estimated_delay_days != null && <MetaRow icon={Clock} label="Délai" value={`${signal.estimated_delay_days} jours`} />}
          </div>

          {/* Community proof */}
          <div className="bg-white border border-[#E4E1D8] rounded-md p-3">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#5B5B62] mb-2">Community Proof</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-[#5B5B62]">Confirmations</span><div className="text-[#0B2545] font-medium">{signal.community_proof.partners_confirmed}</div></div>
              <div><span className="text-[#5B5B62]">Entrevues</span><div className="text-[#0B2545] font-medium">{signal.community_proof.interviews_obtained}</div></div>
              <div><span className="text-[#5B5B62]">Embauches</span><div className="text-[#0B2545] font-medium">{signal.community_proof.hires}</div></div>
              <div><span className="text-[#5B5B62]">Δ score moyen</span><div className="text-[#0B2545] font-medium">{signal.community_proof.avg_score_delta ?? "—"}</div></div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            <button onClick={() => onConfirm(signal.id)} className="flex-1 px-3 py-2 text-sm bg-[#0B2545] text-white rounded-md hover:bg-[#123163]" data-testid="drawer-confirm-btn">
              <CheckCircle2 className="h-4 w-4 inline mr-1" /> Confirmer ce signal
            </button>
            <select
              onChange={(e) => { if (e.target.value) { onOutcome(signal.id, e.target.value); e.target.value=""; } }}
              className="px-2.5 py-2 text-sm border border-[#E4E1D8] rounded-md bg-white"
              data-testid="drawer-outcome-select"
            >
              <option value="">J&apos;ai un résultat…</option>
              <option value="interview_obtained">Entrevue obtenue</option>
              <option value="hired">Embauche / obtention</option>
              <option value="positive">Positif</option>
              <option value="neutral">Neutre</option>
              <option value="negative">Négatif</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <Icon className="h-3.5 w-3.5 text-[#5B5B62]" strokeWidth={1.5} />
      <span className="text-[#5B5B62]">{label}</span>
      <span className="text-[#1F2937] font-medium ml-auto">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Submit modal
// ---------------------------------------------------------------------------
function SubmitModal({ categories, onClose, onSubmitted }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    category: categories[0]?.key || "network_hiring",
    title_fr: "", description_fr: "", city: "", country: "CA",
    urgency: "this_month", cost_level: "free", difficulty_level: "moderate",
    target_public: "all", opportunity_type: "",
    visibility_public: 40, rarity: 60, exclusivity: 60,
    access_difficulty: 55, economic_potential: 65,
    actions_to_take_text: "", resources_text: "",
    economic_impact_estimate: "", estimated_time_hours: "",
  });
  const set = (k, v) => setForm({ ...form, [k]: v });
  const inputCls = "w-full px-2.5 py-1.5 text-sm bg-white border border-[#E4E1D8] rounded-md focus:outline-none focus:border-[#0B2545]";
  const S = FormLabel;

  const submit = async () => {
    if (form.title_fr.length < 8 || form.description_fr.length < 20) {
      toast.error("Titre (>=8) et description (>=20) requis"); return;
    }
    setBusy(true);
    try {
      const actions = form.actions_to_take_text.split("\n").map((s) => s.trim()).filter(Boolean);
      const resources = form.resources_text.split("\n").map((line) => {
        const parts = line.split("|").map((s) => s.trim());
        if (parts.length >= 2) return { label: parts[0], url: parts[1] };
        return null;
      }).filter(Boolean);
      const payload = {
        ...form,
        actions_to_take: actions,
        resources,
        estimated_time_hours: form.estimated_time_hours ? Number(form.estimated_time_hours) : null,
      };
      delete payload.actions_to_take_text;
      delete payload.resources_text;
      const r = await api.post("/discovery/submit", payload);
      toast.success(r.data.message_fr || "Signal soumis");
      onSubmitted();
    } catch (e) {
      toast.error("Échec de la soumission");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="discovery-submit-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E4E1D8] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#5B5B62]">Soumission communautaire</div>
            <h3 className="text-lg font-display text-[#1F2937]">Nouveau signal Discovery</h3>
          </div>
          <button onClick={onClose} data-testid="submit-close" className="p-1 text-[#5B5B62]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div className="col-span-2"><S>Catégorie</S>
            <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="submit-category">
              {categories.map((c) => <option key={c.key} value={c.key}>{c.label_fr}</option>)}
            </select>
          </div>
          <div className="col-span-2"><S>Titre (fr)</S>
            <input className={inputCls} value={form.title_fr} onChange={(e) => set("title_fr", e.target.value)} data-testid="submit-title" />
          </div>
          <div className="col-span-2"><S>Description (fr)</S>
            <textarea className={inputCls + " h-24"} value={form.description_fr} onChange={(e) => set("description_fr", e.target.value)} data-testid="submit-description" />
          </div>
          <div><S>Ville</S><input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="submit-city" /></div>
          <div><S>Pays</S>
            <select className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)}>
              <option value="CA">Canada</option><option value="FR">France</option><option value="MA">Maroc</option>
              <option value="SN">Sénégal</option><option value="CI">Côte d&apos;Ivoire</option><option value="AE">Émirats</option>
            </select>
          </div>
          <div><S>Urgence</S>
            <select className={inputCls} value={form.urgency} onChange={(e) => set("urgency", e.target.value)}>
              {Object.entries(URGENCY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div><S>Coût</S>
            <select className={inputCls} value={form.cost_level} onChange={(e) => set("cost_level", e.target.value)}>
              {Object.entries(COST_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div><S>Difficulté</S>
            <select className={inputCls} value={form.difficulty_level} onChange={(e) => set("difficulty_level", e.target.value)}>
              {Object.entries(DIFFICULTY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div><S>Public cible</S>
            <select className={inputCls} value={form.target_public} onChange={(e) => set("target_public", e.target.value)}>
              <option value="all">Tous</option><option value="newcomer">Nouveaux arrivants</option>
              <option value="student">Étudiants</option><option value="professional">Professionnels</option>
              <option value="entrepreneur">Entrepreneurs</option><option value="senior">Seniors</option>
              <option value="minority">Minorités</option>
            </select>
          </div>
          <div><S>Impact éco. estimé</S><input className={inputCls} placeholder="+10 000 $/an" value={form.economic_impact_estimate} onChange={(e) => set("economic_impact_estimate", e.target.value)} /></div>
          <div><S>Temps estimé (h)</S><input type="number" className={inputCls} value={form.estimated_time_hours} onChange={(e) => set("estimated_time_hours", e.target.value)} /></div>

          {/* Sliders */}
          {[
            ["visibility_public", "Visibilité publique"],
            ["rarity", "Rareté"],
            ["exclusivity", "Exclusivité"],
            ["access_difficulty", "Difficulté d'accès"],
            ["economic_potential", "Potentiel économique"],
          ].map(([k, label]) => (
            <div key={k} className="col-span-1">
              <S>{label} · <span className="text-[#0B2545] font-bold">{form[k]}</span></S>
              <input type="range" min="0" max="100" value={form[k]} onChange={(e) => set(k, Number(e.target.value))} className="w-full" />
            </div>
          ))}

          <div className="col-span-2"><S>Actions à réaliser (une par ligne)</S>
            <textarea className={inputCls + " h-20"} value={form.actions_to_take_text} onChange={(e) => set("actions_to_take_text", e.target.value)} />
          </div>
          <div className="col-span-2"><S>Ressources (label | url — une par ligne)</S>
            <textarea className={inputCls + " h-16"} placeholder="Guide ALTIORA | https://altioracoop.com/guide" value={form.resources_text} onChange={(e) => set("resources_text", e.target.value)} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E4E1D8] flex items-center justify-between">
          <div className="text-[11px] text-[#8B8B8F]">
            Votre Trust Score détermine le tier de validation (immédiate / cross-check / verify).
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-[#5B5B62]">Annuler</button>
            <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm bg-[#0B2545] text-white rounded-md hover:bg-[#123163] disabled:opacity-50" data-testid="submit-send">
              {busy ? "Envoi…" : "Soumettre le signal"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
